import axios from "axios";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/store";
import { setShowModalAuthentication } from "store";
import Swal from "sweetalert2";
import { postRefreshToken } from "services/authServices/authServices";
import { encriptaDatos } from "helpers/herramientas/bcryptjs";

const protocol = window.location.protocol; // Asumiendo que 'protocol' viene del entorno
const tribunalAPI = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});
const electronicoAPI = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL_ELECTRONICO,
});
const accesoAPI = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL_ACCESO_EDIFICIOS,
});
const streamingAPI = axios.create({
  baseURL:  `${protocol}//streaming2.pjedomex.gob.mx/`
});
const electronicoPeritosAPI = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL_ELECTRONICO_PERITOS,
});

const AxiosInterceptor = ({ children }: { children: any }) => {
  const dispatch = useDispatch();
  const { showModalAuthentication } = useSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    const resInterceptor = (response: any) => {
      return response;
    };
    const errInterceptor = (error: any) => {
      if (
        error.response.status === 401 &&
        window.location.pathname !== "/auth/login"
      ) {
        if (!showModalAuthentication) {
          dispatch(setShowModalAuthentication(true));
        }
      }
      return Promise.reject(error);
    };
    const interceptor = tribunalAPI.interceptors.response.use(
      resInterceptor,
      errInterceptor
    );
    return () => tribunalAPI.interceptors.response.eject(interceptor);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (showModalAuthentication && window.location.pathname != "/auth/reset-password") {
      alertAuth();
    }
    // eslint-disable-next-line
  }, [showModalAuthentication]);

  const alertAuth = () => {
    localStorage.removeItem("token");
    Swal.fire({
      icon: "warning",
      title: "Ingresa de nuevo al sistema",
      text: "Tu sesión ha expirado",
      confirmButtonText: "Iniciar sesión",
      allowOutsideClick: false,
      html: `
          <input type="password" id="password" class="swal2-input" placeholder="Password">`,
      preConfirm: () => {
        const input = Swal.getPopup()?.querySelector(
          "#password"
        ) as HTMLInputElement;
        const password = input.value ? encriptaDatos(input.value, 8) : "";
        const user = localStorage.getItem("user");
        return tribunalAPI
          .post("/auth/login", { user, password: password })
          .then((response: any) => {
            localStorage.setItem("token", response.data.data.access_token);
            dispatch(setShowModalAuthentication(false));
            return response;
          })
          .catch((error) => {
            Swal.showValidationMessage(`${error.response?.data?.data}`);
          });
      },
    })
      .then(async (result: any) => {
        const laspath = localStorage.getItem("lastpath");
        window.location.href = laspath ? laspath : "/dashboard";
      })
      .catch((err: any) => {
        console.log(err);
      });
  };
  return children;
};

const getStatusInfoResponse = (status: number) => {
  let icon =
    status >= 300 && status <= 499
      ? { icon: "warning", title: "Advertencia" }
      : (status: number) =>
          499 && status <= 599 ? { icon: "error", title: "Error" } : "";
  return icon;
};

//Interceptors
tribunalAPI.interceptors.request.use(
  function (res: any) {
    const objSuccess = {
      ...res,
      headers: {
        ...res.header,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        Refreshtoken: localStorage.getItem("refresh_token") || "",
      },
    };
    return objSuccess;
  },
  function (error) {
    const status = error.response.status;
    const icon = getStatusInfoResponse(status);

    const objErr = {
      ...error,
      response: {
        ...icon,
        ...error.response,
      },
    };

    return Promise.reject(objErr);
  }
);
accesoAPI.interceptors.request.use(
  function (res: any) {
    const objSuccess = {
      ...res,
      headers: {
        ...res.header,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        Refreshtoken: localStorage.getItem("refresh_token") || "",
      },
    };
    return objSuccess;
  },
  function (error) {
    const status = error.response.status;
    const icon = getStatusInfoResponse(status);

    const objErr = {
      ...error,
      response: {
        ...icon,
        ...error.response,
      },
    };

    return Promise.reject(objErr);
  }
);
electronicoAPI.interceptors.request.use(
  function (res: any) {
    const objSuccess = {
      ...res,
      headers: {
        ...res.header,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        Refreshtoken: localStorage.getItem("refresh_token") || "",
      },
    };
    return objSuccess;
  },
  function (error) {
    const status = error.response.status;
    const icon = getStatusInfoResponse(status);

    const objErr = {
      ...error,
      response: {
        ...icon,
        ...error.response,
      },
    };

    return Promise.reject(objErr);
  }
);

tribunalAPI.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    const prevRequest = error?.config;
    if (
      error.response.status === 401 &&
      !prevRequest.sent &&
      localStorage.getItem("refresh_token") &&
      error.config.url !== "/auth/refresh-token"
    ) {
      localStorage.removeItem("token");
      prevRequest.sent = true;
      postRefreshToken()
        .then(({ data }) => {
          localStorage.setItem("token", data?.data?.access_token);
          localStorage.setItem("refresh_token", data?.data?.refresh_token);
          prevRequest.headers[
            "Authorization"
          ] = `Bearer ${data?.data?.access_token}`;
          return tribunalAPI(prevRequest);
        })
        .catch((err) => {
          console.log(err);
        });
    }
    return Promise.reject(error);
  }
);

export default tribunalAPI;
export { AxiosInterceptor, streamingAPI, accesoAPI, electronicoAPI, electronicoPeritosAPI };
